import { createAsyncThunk } from "@reduxjs/toolkit";
import { companyManagementApi } from "../../api/companyManagementApi";
import { CompanyManagementUser } from "../../types/companyManagementTypes";
import { handleRequest } from "../utils/handleRequest";
import { AppDispatch, RootState } from "../store";
import i18n from "../../i18n";
import {ErrorResponse} from "../../api/Requests";
import {UserManagementErrorCode} from "../../api/userManagementErrorCode";
import {ErrorCode} from "../../api/errorCode";
import {handleUserManagementError} from "../../api/userManagementErrorHandler";

export const fetchCompanyManagementUsersThunk = createAsyncThunk<
  CompanyManagementUser[] | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "companyManagement/fetchUsers",
  async (_, { dispatch }) => {
    const res = await handleRequest<CompanyManagementUser[]>(
      dispatch,
      "indirect",
      () => companyManagementApi.getCompanyManagementUsers()
    );
    return res.success() ? res.result : null;
  }
);

export const createCompanyManagementUserThunk = createAsyncThunk<
  CompanyManagementUser | null,
  { firstName: string; lastName: string; email: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: ErrorResponse }
>(
    "companyManagement/createUser",
    async (userData, { dispatch, rejectWithValue }) => {
        const res = await handleRequest<CompanyManagementUser>(
            dispatch,
            "direct",
            () => companyManagementApi.createCompanyManagementUser(userData),
            i18n.t("requestMessage.userCreated"),
            { suppressErrorCodes: [ErrorCode.ManagerUserCreationFailed] }
        );

        if (res.success()) {
            await dispatch(fetchCompanyManagementUsersThunk());
            return res.result;
        }

        if (res.errorResponse?.errorCode === ErrorCode.ManagerUserCreationFailed) {
            handleUserManagementError(dispatch, res.errorResponse);
        }

        return rejectWithValue(res.errorResponse ?? {
            message: "Unknown Error",
            errorCode: ErrorCode.GeneralError,
            statusCode: 0,
        });
    }
);

export const editCompanyManagementUserThunk = createAsyncThunk<
  CompanyManagementUser | null,
  { id: string; firstName: string; lastName: string },
  { dispatch: AppDispatch; state: RootState }
>(
  "companyManagement/editUser",
  async ({ id, firstName, lastName }, { dispatch }) => {
    const res = await handleRequest<CompanyManagementUser>(
      dispatch,
      "direct",
      () => companyManagementApi.editCompanyManagementUser(id, { firstName, lastName }),
      i18n.t("requestMessage.userUpdated")
    );
    if (res.success()) {
      await dispatch(fetchCompanyManagementUsersThunk());
      return res.result;
    }
    return null;
  }
);

export const deleteCompanyManagementUserThunk = createAsyncThunk<
  string | null,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "companyManagement/deleteUser",
  async (id, { dispatch }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => companyManagementApi.deleteCompanyManagementUser(id),
      i18n.t("requestMessage.userDeleted")
    );
    if (res.success()) {
      await dispatch(fetchCompanyManagementUsersThunk());
      return id;
    }
    return null;
  }
);
